import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import { AppContextProvider } from "./context/AppContext";
import React, { useContext, Suspense } from "react";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import PrivateRoute from "./components/PrivateRoute";
import ScrollToTop from "./components/ScrollToTop";
import Loader from "./components/Loader";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";
import AdminDashboard from "./pages/Auth/AdminDashboard";
import AdminProfile from "./pages/AdminProfile";
import ChangePassword from "./pages/ChangePassword";
import UserManager from "./pages/UserManager";
import CustomIngredientManager from "./pages/CustomIngredientManager";
import ContentManager from "./pages/Content";
import EmailTemplate from "./pages/EmailTemplates";
import ResetPasswordFromLink from "./pages/ResetPasswordFromLink";
import WaitlistManager from "./pages/WaitlistManager";
import IngredientCategoryManager from "./pages/IngredientCategoryManager";
import DiscountCodeManager from "./pages/DiscountCodeManager";
import BillingLogs from "./pages/BillingLogs";
import SubscriptionPlans from "./pages/SubscriptionPlans";
import TrialUsers from "./pages/TrialUsers";
import AuditLogs from "./pages/AuditLogs";
import SubscriptionWithAuditTabs from "./pages/SubscriptionWithAuditTabs";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContextProvider>
          <Suspense fallback={<Loader />}>
            <ScrollToTop />
            <ToastContainer closeOnClick={false} />
            <AppRoutes />
          </Suspense>
        </AppContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

const Layout = () => {
  return (
    <>
      <Outlet />
    </>
  );
};

/** Authenticated app shell + nested routes (only mounted when isLoggedIn is true). */
const privateRoutesTree = (
  <Route
    path="/"
    element={
      <PrivateRoute>
        <Layout />
      </PrivateRoute>
    }
  >
    <Route index element={<Navigate to="/dashboard" replace />} />
    <Route path="dashboard" element={<AdminDashboard />} />
    <Route path="user-manager" element={<UserManager />} />
    <Route path="custom-ingredients" element={<CustomIngredientManager />} />
    <Route path="ingredient-categories" element={<IngredientCategoryManager />} />
    <Route path="discount-codes" element={<DiscountCodeManager />} />
    <Route path="content-manager" element={<ContentManager />} />
    <Route path="email-templates" element={<EmailTemplate />} />
    <Route path="waitlist-manager" element={<WaitlistManager />} />
    <Route path="profile" element={<AdminProfile />} />
    <Route path="change-password" element={<ChangePassword />} />
    <Route path="billing-logs" element={<BillingLogs />} />
    <Route path="subscription-plans" element={<SubscriptionWithAuditTabs />} />
    <Route path="trial-users" element={<TrialUsers />} />
    <Route path="audit-logs" element={<AuditLogs />} />
  </Route>
);

const hasPersistedSession = () =>
  Boolean(
    typeof window !== "undefined" &&
      localStorage.getItem("token") &&
      localStorage.getItem("userProfile")
  );

const AppRoutes = () => {
  const { isLoggedIn } = useContext(AuthContext);
  // Match AuthContext + storage so we never mount the private *and* public trees (duplicate `path="/"`).
  // Also if context is stale but storage was cleared, use the public tree (avoids redirect loops).
  const authed = isLoggedIn && hasPersistedSession();

  if (authed) {
    return (
      <Routes>
        {privateRoutesTree}
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/register" element={<Navigate to="/dashboard" replace />} />
        <Route path="/reset-password" element={<ResetPasswordFromLink />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<SignIn />} />
      <Route path="/register" element={<SignUp />} />
      <Route path="/reset-password" element={<ResetPasswordFromLink />} />
      {/* Logged out but still on /dashboard, /user-manager, etc. → login (avoids Error-page flash after logout) */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default App;
